import { Injectable } from '@angular/core';
import { Camera, CameraResultType, Photo } from '@capacitor/camera';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { ExamdojoSupabaseService } from '@examdojo/supabase';
import { randUuid } from '@ngneat/falso';
import { heicTo } from 'heic-to';
import { convertFileToDataUrl, dataURLtoFileBlob } from '../utils';
import {
  DEFAULT_UPLOAD_IMAGE_OPTIONS,
  ImageUploadResult,
  ProcessedImage,
  UploadImageOptions,
} from './capacitor-image.model';

@Injectable({ providedIn: 'root' })
export class CapacitorImageService {
  constructor(
    private readonly supabaseService: ExamdojoSupabaseService,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {}

  async takePicture(): Promise<null | Photo> {
    try {
      return await Camera.getPhoto({
        quality: 100,
        allowEditing: true,
        resultType: CameraResultType.DataUrl,
      });
    } catch (error) {
      return null;
    }
  }

  async fileToPhoto(file: File): Promise<Photo> {
    const base64 = await convertFileToDataUrl(file);
    return {
      dataUrl: base64,
      format: file.type.split('/')[1],
      saved: false,
    };
  }

  async uploadImage(
    bucketName: string,
    image: Photo,
    fileName: string,
    options: UploadImageOptions = DEFAULT_UPLOAD_IMAGE_OPTIONS,
  ): Promise<ImageUploadResult> {
    if (!image.dataUrl) {
      throw new Error('DataUrl is required. Image might not be taken with correct resultType');
    }

    // Delete the image metadata
    delete image.exif;

    const { convertHeicToJpg, maxDimension } = { ...DEFAULT_UPLOAD_IMAGE_OPTIONS, ...options };

    let fileType = `image/${image.format}`;

    let fileBlob: Blob = await dataURLtoFileBlob(image.dataUrl, fileType);

    if (convertHeicToJpg && image.format === 'heic') {
      try {
        fileBlob = await heicTo({
          blob: fileBlob,
          quality: 100,
          type: 'image/jpeg',
        });
        fileType = 'image/jpeg';
        fileName = fileName.replace(/\.heic$/i, '.jpg');
      } catch (error) {
        this.errorHandlerService.error('Failed to convert heic to jpg', {
          err: error,
          context: {
            blobSize: fileBlob.size,
            blobType: fileBlob.type,
          },
        });
      }
    }

    const processed = await this.processImage(fileBlob, maxDimension);
    const path = await this.supabaseService.uploadFile(bucketName, processed.blob, fileType, fileName);

    return { path, dimensions: processed.dimensions };
  }

  async processImage(blob: Blob, maxDimension?: number): Promise<ProcessedImage> {
    if (blob.type === 'image/svg+xml') {
      return { blob, dimensions: { width: 0, height: 0 } };
    }

    const img = await createImageBitmap(blob);
    const originalWidth = img.width;
    const originalHeight = img.height;

    if (!maxDimension || (img.width <= maxDimension && img.height <= maxDimension)) {
      img.close();
      return { blob, dimensions: { width: originalWidth, height: originalHeight } };
    }

    let newWidth: number;
    let newHeight: number;
    if (img.width >= img.height) {
      newWidth = maxDimension;
      newHeight = Math.floor((img.height / img.width) * maxDimension);
    } else {
      newHeight = maxDimension;
      newWidth = Math.floor((img.width / img.height) * maxDimension);
    }

    const canvas = document.createElement('canvas');
    canvas.width = newWidth;
    canvas.height = newHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      img.close();
      return { blob, dimensions: { width: originalWidth, height: originalHeight } };
    }

    ctx.drawImage(img, 0, 0, newWidth, newHeight);
    img.close();

    return new Promise<ProcessedImage>((resolve, reject) => {
      canvas.toBlob(
        (resizedBlob) => {
          if (resizedBlob) {
            resolve({ blob: resizedBlob, dimensions: { width: newWidth, height: newHeight } });
          } else {
            reject(new Error('Canvas to Blob conversion failed'));
          }
        },
        blob.type,
        0.9,
      );
    });
  }

  getFileName(userId: string, format: string): string {
    return `${userId}/${randUuid()}.${format}`;
  }

  getSignedUrlForFile(bucketName: string, fileName: string, expiresIn?: number): Promise<string> {
    return this.supabaseService.getSignedUrlForFile(bucketName, fileName, expiresIn);
  }

  getPublicUrl(bucketName: string, fileName: string): string {
    return this.supabaseService.getPublicUrl(bucketName, fileName);
  }
}
